import { useCallback } from 'react';
import { useUserDataQuery } from '@shared/queries';
import { SupportedCurrency, CURRENCY_LOCALE_MAP } from './currency.types';

export const formatAmount = (
  amount: number,
  currency: SupportedCurrency,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
): string => {
  const locale = CURRENCY_LOCALE_MAP[currency] ?? 'cs-CZ';
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/** Maps ISO currency codes to their preferred display symbols. */
const CURRENCY_DISPLAY_SYMBOL: Partial<Record<SupportedCurrency, string>> = {
  CZK: 'Kč',
};

export const useCurrency = () => {
  const { data: userData } = useUserDataQuery();
  const currencyCode = (userData?.preferredCurrency as SupportedCurrency) ?? 'CZK';
  const preferredCurrency = CURRENCY_DISPLAY_SYMBOL[currencyCode] ?? currencyCode;

  return { currencyCode, preferredCurrency };
};

export const useFormatCurrency = (currencyOverride?: SupportedCurrency | null) => {
  const { currencyCode: userCurrencyCode, preferredCurrency: userPreferredCurrency } = useCurrency();
  const currencyCode = currencyOverride ?? userCurrencyCode;
  const preferredCurrency = currencyOverride
    ? (CURRENCY_DISPLAY_SYMBOL[currencyOverride] ?? currencyOverride)
    : userPreferredCurrency;

  const format = useCallback(
    (
      amount: number | string | undefined | null,
      _fromCurrency?: string,
      minimumFractionDigits?: number,
      maximumFractionDigits?: number,
    ): string => {
      if (amount === undefined || amount === null || amount === '')
        return formatAmount(0, currencyCode, minimumFractionDigits, maximumFractionDigits);
      const num = typeof amount === 'string' ? parseFloat(amount.replace(',', '.')) : amount;
      if (isNaN(num)) return '';

      return formatAmount(num, currencyCode, minimumFractionDigits, maximumFractionDigits);
    },
    [currencyCode],
  );

  return { format, preferredCurrency };
};
